import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useHospitalData } from '../../hooks/useHospitalData'
import LineChart, { LineChartHandle } from '../../components/Charts/LineChart'
import CustomSelect from '../../components/Common/CustomSelect'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { sortTimePeriods, trimTrailingEmptyPeriods, filterFutureMonths, getStratificationColors, normalizeStratGroup, createChartExportHandlers, createMenuToggle, setupMenuClickOutside } from '../../utils/exportUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import './PatientEducation.css'

const PatientEducation: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const { data: data1, loading: loading1, initialLoading: initialLoading1 } = useHospitalData(
        '/api/pmh/process/patient_education',
        selectedHospitalIds
    )

    const { data: data2, loading: loading2, initialLoading: initialLoading2 } = useHospitalData(
        '/api/pmh/process/patient_education_strat',
        selectedHospitalIds
    )

    const chart1Ref = useRef<LineChartHandle>(null)
    const chart1ContainerRef = useRef<HTMLDivElement>(null)
    const chart2Ref = useRef<LineChartHandle>(null)
    const chart2ContainerRef = useRef<HTMLDivElement>(null)

    const menu1Id = 'export-menu-patient-ed-1'
    const toggleMenu1 = createMenuToggle(menu1Id)
    const chart1Exports = createChartExportHandlers(
        chart1ContainerRef,
        'Patient Education - PMH Conditions, IL MOMS Line, MAR Now',
        'Patient_Education_Overall',
        { fontBoost: true, exportTitle: 'Patient Education - PMH Conditions, IL MOMS Line, MAR Now' }
    )

    const menu2Id = 'export-menu-patient-ed-2'
    const toggleMenu2 = createMenuToggle(menu2Id)
    const chart2Exports = createChartExportHandlers(
        chart2ContainerRef,
        'Patient Education - Stratified',
        'Patient_Education_Stratified',
        { fontBoost: true, exportTitle: 'Patient Education - Stratified' }
    )

    useEffect(() => {
        const cleanup1 = setupMenuClickOutside(() => chart1Exports.closeMenu(menu1Id));
        const cleanup2 = setupMenuClickOutside(() => chart2Exports.closeMenu(menu2Id));
        return () => {
            cleanup1();
            cleanup2();
        };
    }, []);

    const safeParseEducationPct = (value: any): number | null => {
        if (value === null || value === undefined || value === '' || value === '""') {
            return null
        }

        if (value === 0 || value === "0") {
            return 0
        }

        const num = parseFloat(value)
        return isNaN(num) ? null : num
    }

    const [interval1, setInterval1] = usePersistedState('pmh_patient_edu_interval1', 'monthly')
    const [chart1Data, setChart1Data] = useState<{ categories: string[], series: any[] }>({ categories: [], series: [] })
    const [chart1NoDataMessage, setChart1NoDataMessage] = useState<string | undefined>(undefined)

    const [interval2, setInterval2] = usePersistedState('pmh_patient_edu_interval2', 'monthly')
    const [stratType, setStratType] = usePersistedState('pmh_patient_edu_stratType', 'insurance')
    const [chart2Data, setChart2Data] = useState<{ categories: string[], series: any[], colors: string[] }>({ categories: [], series: [], colors: [] })
    const [chart2NoDataMessage, setChart2NoDataMessage] = useState<string | undefined>(undefined)

    useEffect(() => {
        if (!data1) {
            setChart1Data({ categories: [], series: [] })
            setChart1NoDataMessage("No Data Available")
            return
        }

        const rows = data1?.response?.result?.rows || []
        const columns = data1?.response?.result?.column_order || []

        if (rows.length === 0) {
            setChart1Data({ categories: [], series: [] })
            setChart1NoDataMessage("No Data Available")
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const stratIdx = columns.indexOf('stratification') !== -1 ? columns.indexOf('stratification') : 3

        const filteredRows = rows.filter((row: any[]) => {
            const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 2
            return row[periodIdx] && row[1] === intervalMap[interval1] && row[stratIdx] === 'Overall'
        })

        if (filteredRows.length === 0) {
            setChart1Data({ categories: [], series: [] })
            setChart1NoDataMessage("No Data Available")
            return
        }

        const periodData: Record<string, (number | null)[]> = {}

        const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 2

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[periodIdx])
            const val = safeParseEducationPct(row[columns.indexOf('education_pct')])

            if (timePeriod) {
                if (!periodData[timePeriod]) periodData[timePeriod] = []
                periodData[timePeriod].push(val)
            }
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Object.keys(periodData), interval1))
        const seriesData: (number | null)[] = []

        sortedPeriods.forEach(period => {
            const vals = periodData[period]
            const validValues = vals.filter(v => v !== null) as number[]
            const avg = validValues.length ? validValues.reduce((a, b) => a + b, 0) / validValues.length : null
            seriesData.push(avg)
        })

        const hasValidData = seriesData.some(val => val !== null)

        if (!hasValidData) {
            setChart1Data({ categories: sortedPeriods, series: [{ name: 'Patient Education', data: sortedPeriods.map(() => null) }] })
            setChart1NoDataMessage("No Data Available")
            return
        }

        setChart1NoDataMessage(undefined)
        const trimmed = trimTrailingEmptyPeriods(sortedPeriods, [{ name: 'Patient Education', data: seriesData }])
        setChart1Data({
            categories: trimmed.categories,
            series: trimmed.series
        })

    }, [data1, interval1, isMultiSelect])

    useEffect(() => {
        if (!data2) {
            setChart2Data({ categories: [], series: [], colors: [] })
            setChart2NoDataMessage("No Data Available")
            return
        }

        const rows = data2?.response?.result?.rows || []
        const columns = data2?.response?.result?.column_order || []

        if (rows.length === 0) {
            setChart2Data({ categories: [], series: [], colors: [] })
            setChart2NoDataMessage("No Data Available")
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const stratMap: Record<string, string> = {
            'insurance': 'Insurance',
            'bipoc': 'Race/Ethnicity',
            'allrace': 'Race/Ethnicity (Detailed)',
            'language': 'Primary Language'
        }

        const filteredRows = rows.filter((row: any[]) => {
            const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 2
            return row[periodIdx] && row[1] === intervalMap[interval2] &&
                row[3] === stratMap[stratType]
        })

        if (filteredRows.length === 0) {
            setChart2Data({ categories: [], series: [], colors: [] })
            setChart2NoDataMessage("No Data Available")
            return
        }

        const groups: Record<string, Record<string, (number | null)[]>> = {}
        const allPeriods = new Set<string>()

        const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 2

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[periodIdx])
            const rawGroupName = String(row[4]).trim()
            const groupName = normalizeStratGroup(stratType, rawGroupName) || rawGroupName

            if (timePeriod && groupName) {
                allPeriods.add(timePeriod)

                const val = safeParseEducationPct(row[columns.indexOf('education_pct')])

                if (!groups[groupName]) groups[groupName] = {}
                if (!groups[groupName][timePeriod]) groups[groupName][timePeriod] = []
                groups[groupName][timePeriod].push(val)
            }
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Array.from(allPeriods), interval2))
        const series: any[] = []

        let sortedGroupNames: string[]
        if (stratType === 'insurance') {
            sortedGroupNames = ['Private', 'Public'].filter(g => Object.keys(groups).includes(g))
        } else if (stratType === 'bipoc') {
            sortedGroupNames = ['BIPOC', 'White'].filter(g => Object.keys(groups).includes(g))
        } else if (stratType === 'allrace') {
            sortedGroupNames = ['Black/African American', 'White', 'Hispanic', 'Asian', 'Race Not Documented', 'Other']
        } else {
            sortedGroupNames = Object.keys(groups).sort()
        }

        sortedGroupNames.forEach(group => {
            const groupData = sortedPeriods.map(period => {
                const vals = groups[group]?.[period]
                if (!vals || vals.length === 0) return null

                const validValues = vals.filter(v => v !== null) as number[]
                if (validValues.length === 0) return null

                return validValues.reduce((a, b) => a + b, 0) / validValues.length
            })
            series.push({ name: group, data: groupData })
        })

        const colorKey = stratType === 'bipoc' ? 'race' : (stratType === 'allrace' ? 'race_all' : stratType)
        const colors = getStratificationColors(colorKey)

        const allNull = series.length > 0 && series.every(s => s.data.every((v: any) => v === null))

        if (allNull) {
            setChart2Data({ categories: sortedPeriods, series, colors })
            setChart2NoDataMessage("No Data Available")
            return
        }

        setChart2NoDataMessage(undefined)
        const trimmed2 = trimTrailingEmptyPeriods(sortedPeriods, series)
        setChart2Data({
            categories: trimmed2.categories,
            series: trimmed2.series,
            colors: colors
        })

    }, [data2, interval2, stratType, isMultiSelect])

    if (initialLoading1 || initialLoading2) return <LoadingNotice title="Loading patient education data..." />

    return (
        <div id={generatePDFIds.dashboard()} className="patient-education-page">
            <div className="page-header">
                <h1 className="page-title">Process Measures</h1>
                <div className="header-controls">
                    <CustomSelect
                        value={interval1}
                        onChange={setInterval1}
                        options={[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'bi-yearly', label: 'Bi-Yearly' },
                            { value: 'yearly', label: 'Yearly' }
                        ]}
                    />
                </div>
            </div>

            <div id={generatePDFIds.chart(1)} className="chart-section" ref={chart1ContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">Patient Education - PMH Conditions, IL MOMS Line, MAR Now</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu1}>&#9776;</button>
                        <div id={menu1Id} className="export-dropdown">
                            <div onClick={() => { chart1Exports.handleDownloadSVG(menu1Id); }}>Download SVG</div>
                            <div onClick={() => { chart1Exports.handleDownloadPNG(menu1Id); }}>Download PNG</div>
                            <div onClick={() => { chart1Ref.current?.exportToCSV(); chart1Exports.closeMenu(menu1Id); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of patient charts with documentation of patient receiving education before delivery hospitalization
                    discharge on postpartum PMH conditions, IL MOMS Line, and MAR Now.
                </p>
                <LineChart
                    ref={chart1Ref}
                    categories={chart1Data.categories}
                    series={chart1Data.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="Patient Education %"
                    xAxisLabel="Time Period"
                    colors={["#0072B2"]}
                    noDataMessage={chart1NoDataMessage}
                    showXAxisTooltip={true}
                    isLoading={loading1}
                    decimals={1}
                    exportFilename="Patient_Education_Overall"
                />
                <p className="data-source">Data from hospital level data form</p>
            </div>

            <div className="strat-controls-container">
                <CustomSelect
                    value={stratType}
                    onChange={setStratType}
                    options={[
                        { value: 'insurance', label: 'Insurance Type' },
                        { value: 'bipoc', label: 'Race/Ethnicity (BIPOC & White)' },
                        { value: 'allrace', label: 'Race/Ethnicity (All Groups)' },
                        { value: 'language', label: 'Language' }
                    ]}
                />
                <CustomSelect
                    value={interval2}
                    onChange={setInterval2}
                    options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' },
                        { value: 'bi-yearly', label: 'Bi-Yearly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                />
            </div>

            <div id={generatePDFIds.chart(2)} className="chart-section" ref={chart2ContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">Patient Education - Stratified</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu2}>&#9776;</button>
                        <div id={menu2Id} className="export-dropdown">
                            <div onClick={() => { chart2Exports.handleDownloadSVG(menu2Id); }}>Download SVG</div>
                            <div onClick={() => { chart2Exports.handleDownloadPNG(menu2Id); }}>Download PNG</div>
                            <div onClick={() => { chart2Ref.current?.exportToCSV(); chart2Exports.closeMenu(menu2Id); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of patient charts with documentation of receiving education before delivery hospitalization discharge
                    on postpartum PMH conditions, IL MOMS Line, and MAR Now stratified by {stratType.replace('_', ' ')}.
                </p>
                <LineChart
                    ref={chart2Ref}
                    categories={chart2Data.categories}
                    series={chart2Data.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="Patient Education %"
                    xAxisLabel="Time Period"
                    colors={chart2Data.colors}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={chart2NoDataMessage}
                    showXAxisTooltip={true}
                    isLoading={loading2}
                    decimals={1}
                    exportFilename="Patient_Education_Stratified"
                />
                <p className="data-source">Data from hospital level data form</p>
            </div>
        </div>
    )
}

export default PatientEducation
